import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { browserHistory } from 'react-router';

import { IconButton, Menu, MenuItem } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { compact, get, isEmpty, map } from 'lodash';
import moment from 'moment';

import { Patient } from '@lib/resources/types';

import DialogReminderSend from './DialogReminderSend';
import { IconButtonProps } from './IconButton';

const messages = defineMessages({
  menuOptionSend: {
    id: 'components.v2.IconButtonReminderActions.menuOptionSend',
    defaultMessage: 'Send now',
  },
  menuOptionPatient: {
    id: 'components.v2.IconButtonReminderActions.menuOptionPatient',
    defaultMessage: 'Go to patient',
  },
  menuOptionAppointment: {
    id: 'components.v2.IconButtonReminderActions.menuOptionAppointment',
    defaultMessage: 'Go to appointment',
  },
  menuOptionReferral: {
    id: 'components.v2.IconButtonReminderActions.menuOptionReferral',
    defaultMessage: 'Go to referral',
  },
  menuOptionCustomAction: {
    id: 'components.v2.IconButtonReminderActions.menuOptionCustomAction',
    defaultMessage: 'Go to custom action',
  },
});

export enum IconButtonReminderActionsActionKey {
  Send,
  GoToPatient,
  GoToAppointment,
  GoToReferral,
  GoToCustomAction,
}

type TReminder = {
  _id: string;
  sendAt: string;
  oid_appointment?: string;
  patient?: Partial<Patient>;
};

export interface IconButtonReminderActionsProps extends Omit<IconButtonProps, 'classes'> {
  reminder: TReminder;
  actions: IconButtonReminderActionsActionKey[];
  onSuccess?: () => void;
}

const IconButtonReminderActions: React.FC<IconButtonReminderActionsProps> = ({
  reminder,
  actions = [
    IconButtonReminderActionsActionKey.Send,
    IconButtonReminderActionsActionKey.GoToPatient,
    IconButtonReminderActionsActionKey.GoToAppointment,
    IconButtonReminderActionsActionKey.GoToReferral,
    IconButtonReminderActionsActionKey.GoToCustomAction,
  ],
  onSuccess,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<any>();
  const [dialogReminderSendOpen, setDialogReminderSendOpen] = useState(false);

  const menuItems = compact(
    map(actions, (actionKey) => {
      let element: JSX.Element | JSX.Element[] | undefined;
      switch (actionKey) {
        case IconButtonReminderActionsActionKey.Send: {
          if (moment(reminder.sendAt as string).isAfter()) {
            element = (
              <MenuItem
                key={actionKey}
                data-lh-id="button-reminder-actions-sendNow"
                onClick={() => {
                  setMoreMenuAnchor(undefined);
                  setDialogReminderSendOpen(true);
                }}
              >
                {formatMessage(messages.menuOptionSend)}
              </MenuItem>
            );
          }
          break;
        }
        case IconButtonReminderActionsActionKey.GoToPatient: {
          const patientId = get(reminder, 'patient._id');
          if (patientId) {
            element = (
              <MenuItem
                data-lh-id="button-reminder-actions-goToPatient"
                key={actionKey}
                onClick={() => browserHistory.push(`/patients/${patientId}/chat`)}
              >
                {formatMessage(messages.menuOptionPatient)}
              </MenuItem>
            );
          }
          break;
        }
        case IconButtonReminderActionsActionKey.GoToAppointment: {
          const patientId = get(reminder, 'patient._id') || get(reminder, 'patient');
          const appointmentId = get(reminder, `appointment._id`) || get(reminder, 'appointment');

          if (patientId && appointmentId) {
            element = (
              <MenuItem
                key={actionKey}
                data-lh-id="button-reminder-actions-goToAppointment"
                onClick={() => browserHistory.push(`/patients/${patientId}/appointments?id=${appointmentId}`)}
              >
                {formatMessage(messages.menuOptionAppointment)}
              </MenuItem>
            );
          }
          break;
        }
        case IconButtonReminderActionsActionKey.GoToReferral: {
          const patientId = get(reminder, 'patient._id') || get(reminder, 'patient');
          const referralId = get(reminder, 'referral._id') || get(reminder, 'referral');

          if (patientId && referralId) {
            element = (
              <MenuItem
                data-lh-id="button-reminder-actions-goToReferral"
                key={actionKey}
                onClick={() => browserHistory.push(`/inbound-referrals/${referralId}`)}
              >
                {formatMessage(messages.menuOptionReferral)}
              </MenuItem>
            );
          }
          break;
        }
        case IconButtonReminderActionsActionKey.GoToCustomAction: {
          const patientId = get(reminder, 'patient._id') || get(reminder, 'patient');
          const customActionId = get(reminder, 'followup._id') || get(reminder, 'followup');
          const appointmentId = get(reminder, 'appointment._id') || get(reminder, 'appointment');
          element = [];

          if (customActionId) {
            element.push(
              <MenuItem
                key={`${actionKey}-customAction`}
                data-lh-id="button-reminder-actions-goToCustomAction"
                onClick={() =>
                  browserHistory.push(`/v2/settings/utilities/custom-actions/appointment/${customActionId}`)
                }
              >
                {formatMessage(messages.menuOptionCustomAction)}
              </MenuItem>,
            );
          }

          if (appointmentId) {
            element.push(
              <MenuItem
                key={`${actionKey}-appt`}
                data-lh-id="button-reminder-actions-goToAppointmentCA"
                onClick={() => browserHistory.push(`/patients/${patientId}/appointments?id=${appointmentId}`)}
              >
                {formatMessage(messages.menuOptionAppointment)}
              </MenuItem>,
            );
          }
          break;
        }
        default:
          break;
      }
      return element;
    }),
  );

  if (!reminder || isEmpty(menuItems)) {
    return null;
  }
  return (
    <>
      <IconButton
        aria-label="More"
        aria-owns={moreMenuAnchor ? `more-menu-${reminder._id}` : undefined}
        aria-haspopup="true"
        data-lh-id="button-reminder-actions-open"
        onClick={(event) => setMoreMenuAnchor(event.currentTarget)}
        {...rest}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id={`more-menu-${reminder._id}`}
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(undefined)}
      >
        {menuItems}
      </Menu>
      <DialogReminderSend
        reminder={reminder}
        open={dialogReminderSendOpen}
        onClose={() => setDialogReminderSendOpen(false)}
        onSuccess={onSuccess}
      />
    </>
  );
};

export default IconButtonReminderActions;
